import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, isAxiosError } from 'axios';

import { CamelCaseKeys, camelcaseKeys, JSONData } from '@uplift/types';

import { ServerError } from './errors';
import { requestsCache } from './requestsCache';
import * as resources from './resources';


export type IAuthStore = {
  hasAccessToken: boolean;
  tokens: {
    accessToken?: string;
    refreshToken?: string;
  };
  getTokens(): void;
  setTokensInLocalStorage(): void;
  logout(redirectUrl?: string, opts?: {
    redirectAfterLogin?: string | boolean;
  }): void;
};


declare module 'axios' {
  export interface AxiosRequestConfig {
    refreshAuth?: boolean;
    authRetryConfig?: {
      maxRetries?: number;
      retryCount?: number;
    };
  }
}


type JsonAllowedRequestValue = boolean | number | string | string[] | string[][] | {[key: string]: unknown} | {[key: string]: unknown}[];
type JsonRequestInput = Record<string, JsonAllowedRequestValue | Record<string, JsonAllowedRequestValue>>;

type FormDataAllowedRequestValue = string | number | string[] | boolean | Blob;
type FormDataRequestInput = Record<string, FormDataAllowedRequestValue>;

type RequestInput = FormDataRequestInput | JsonRequestInput | null;
type RequestOutput = JSONData;

export class RestClient {
  private apiEndpoint: string;
  private authStore: IAuthStore;
  private client: AxiosInstance;

  constructor(apiEndpoint = '') {
    this.authStore = {} as IAuthStore;
    this.apiEndpoint = apiEndpoint;

    this.client = axios.create({
      baseURL: this.apiEndpoint ? this.apiEndpoint : undefined,
      method: 'GET',
      responseType: 'json',
      timeout: 30000,
    });
  }

  private getAuthHeaders = () => {
    const accessToken = this.authStore?.tokens?.accessToken;

    if (accessToken) {
      return {
        Authorization: `Bearer ${accessToken}`
      };
    }

    return {};
  };

  private getBearerToken = () => `Bearer ${this.authStore?.tokens?.accessToken}`;

  _authRefreshToken = async (refreshToken: string) => {
    return requestsCache.cached(
      'auth/refresh',
      { refreshToken },
      () => this.request<{
        access_token: string;
        refresh_token: string;
      }>('/auth/refresh', {
        method: 'POST',
        data: {
          refresh_token: refreshToken,
        },
      })
    );
  };

  async refreshToken(force = false) {
    this.authStore.getTokens();
    if (!this.authStore.hasAccessToken || force) {
      const refreshToken = this.authStore.tokens.refreshToken;
      const refreshResponse = await this._authRefreshToken(refreshToken ?? '');

      if (refreshResponse?.accessToken) {
        this.authStore.tokens.accessToken = refreshResponse.accessToken;
        this.authStore.tokens.refreshToken = refreshResponse.refreshToken;
        this.authStore.setTokensInLocalStorage();
      } else {
        // this.authStore.logout(this.logoutRedirect || '/', {
        this.authStore.logout('/', {
          redirectAfterLogin: true,
        });
      }
    }
  }

  configure = ({ apiEndpoint, authStore }: { apiEndpoint?: string; authStore?: IAuthStore }) => {
    if (apiEndpoint) {
      this.apiEndpoint = apiEndpoint;
    }
    if (authStore) {
      this.authStore = authStore;
    }

    this.client = axios.create({
      baseURL: this.apiEndpoint,
      method: 'GET',
      responseType: 'json',
      timeout: 30000,
      authRetryConfig: {
        maxRetries: 3,
        retryCount: 0
      }
    });

    this.client.interceptors.request.use(
      config => {
        config.headers.Authorization = this.getBearerToken();

        return config;
      },
      err => err
    );

    this.client.interceptors.response.use(
      response => response,
      async (error: unknown) => {
        if (isAxiosError(error)) {
          if (!error.config) return;

          const originalConfig = error.config;

          if (originalConfig.authRetryConfig) {
            const { maxRetries, retryCount } = originalConfig.authRetryConfig;

            // eslint-disable-next-line
            // @ts-ignore-error
            if (retryCount >= maxRetries) {
              return Promise.reject(error);
            }
          }

          if (error.response?.status === 401 && originalConfig.refreshAuth) {
            await this.refreshToken(true);

            if (originalConfig) {
              originalConfig.headers.Authorization = this.getBearerToken();
              if (originalConfig.authRetryConfig && originalConfig.authRetryConfig.retryCount != null) {
                originalConfig.authRetryConfig.retryCount += 1;
              }

              return this.client(originalConfig);
            }
          }
        }

        throw error;
      }
    );
  };

  rawRequest = async <TResponse>(url: string, config: AxiosRequestConfig = {}, refreshAuth = true): Promise<TResponse> => {
    const response = await this.client.request<TResponse>({
      url,
      headers: this.getAuthHeaders(),
      refreshAuth,
      ...config // override config if needed (eg. with method type or request data)
    });

    return response.data;
  };

  request = async <TResponse extends RequestOutput>(url: string, config: AxiosRequestConfig = {}, refreshAuth = true) => {
    const result = await this.rawRequest<TResponse>(url, config, refreshAuth);

    return camelcaseKeys(result);
  };

  healthCheck = async (): Promise<void> => {
    const response = await this.request<{ status: string }>('/healthz');

    if (response.status !== 'success') {
      throw new ServerError('backend unhealthy');
    }
  };

  adminUpcomingSessionsReport = async (tillDate: string) =>
    await this.request<resources.UpcomingSessionsReport>('/api/v1/admin/reports/upcoming_sessions', {
      params: { till_date: tillDate, }
    });

  generateFormData = (data: FormDataRequestInput) => {
    const formData = new FormData();

    Object.keys(data).forEach(key => {
      const value = data[key];

      if (typeof value === 'number') {
        formData.append(key, value.toString());
      } else if (typeof value === 'boolean') {
        formData.append(key, value ? 'true' : 'false');
      } else if (Array.isArray(value)) {
        value.forEach(v => {
          formData.append(key, v);
        });
      } else {
        formData.append(key, value);
      }
    });

    return formData;
  };

  createRawRequest = <TRequestInput extends RequestInput, TRequestOutput extends RequestOutput>(config: AxiosRequestConfig & { formData?: boolean }) =>
    async (data: TRequestInput) => {
      const dataKey = config.method === 'GET' ? 'params' : 'data';
      const _data = config?.formData === true
        ? this.generateFormData(data as FormDataRequestInput)
        : data;

      return await this.rawRequest<TRequestOutput>(config.url ?? '', {
        ...config,
        [dataKey]: _data
      });
    };

  createRequest = <TRequestInput extends RequestInput, TRequestOutput extends RequestOutput>(config: AxiosRequestConfig & { formData?: boolean }) => {
    const createdRequest = this.createRawRequest<TRequestInput, TRequestOutput>(config);

    return async (data: TRequestInput) =>
      camelcaseKeys(await createdRequest(data));
  };

  fileRequest = <TResponse>(url: string, config: AxiosRequestConfig = {}, refreshAuth = true): Promise<AxiosResponse<TResponse>> =>
    this.client.request<TResponse>({
      url,
      headers: this.getAuthHeaders(),
      refreshAuth,
      ...config // override config if needed (eg. with method type or request data)
    });

  /**
   * Sends a request to the specified URL and optionally processes the response using a callback function.
   * This can be used to add mocked data to the API response.
   */
  requestAndMock = async <TResponse extends RequestOutput = null>(url: string, config: AxiosRequestConfig = {}, callback: (data: CamelCaseKeys<TResponse>) => CamelCaseKeys<TResponse> ): Promise<CamelCaseKeys<TResponse>> => {
    const result = await this.request<TResponse>(url, config);

    if (callback) {
      return callback(result) as CamelCaseKeys<TResponse>;
    }
    return result as CamelCaseKeys<TResponse>;
  };
}

export const restClient = new RestClient();
